/**
 * مرحله‌ی شفاف‌سازی — حداکثر سه پرسش.
 *
 * هر پرسش یک گزینه‌ی «اطلاعاتش را ندارم» دارد. آن گزینه `null` می‌فرستد، نه
 * رشته‌ی خالی: نامعلوم یک **پاسخ صریح** است که خط لوله آن را به فرض
 * برچسب‌خورده تبدیل می‌کند، در حالی که رشته‌ی خالی یعنی کاربر فیلد را رد کرده.
 *
 * پاسخ ندادن فرایند را متوقف نمی‌کند؛ دکمه همیشه فعال است.
 */

import { ArrowLeft, RotateCcw } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Card, SectionHeader } from '@/components/ui/primitives'
import { fa } from '@/lib/format'

import type { ClarificationQuestion } from '@/lib/types'

interface ClarificationStepProps {
  questions: ClarificationQuestion[]
  /**
   * پاسخ حفظ‌شده‌ی هر پرسش از تلاش قبل، به ترتیب پرسش‌ها.
   *
   * ⚠️ در بازسازی، سرور پاسخ‌های قبلی را بر اساس هویت پرسش منتقل می‌کند و فقط
   * برای پرسش تازه می‌ایستد. پیش‌پر کردن همان انتقال را دیدنی می‌کند؛ بدون آن
   * کاربر فیلد خالی می‌دید و فکر می‌کرد پاسخش فراموش شده است.
   */
  prefill?: (string | null)[]
  /**
   * تعداد پاسخ‌هایی که حفظ شده‌اند اما در این دور **پرسیده نشده‌اند**.
   *
   * ⚠️ **چرا جدا از `prefill`:** در اجرای واقعی دیدیم شایع‌ترین حالت همین است —
   * بررسی تازه پرسش‌های دیگری دارد، پس هیچ فیلدی پیش‌پر نمی‌شود و صفحه سکوت
   * می‌کرد؛ در حالی که سه واقعیتِ دور قبل واقعاً در زمینه حاضر بودند. آن سکوت
   * همان برداشتِ «یادش نیست جواب دادم» را می‌ساخت، حتی وقتی سامانه فراموش
   * نکرده بود.
   */
  carriedCount?: number
  onSubmit: (answers: (string | null)[]) => void
  onReset: () => void
  isBusy: boolean
}

interface AnswerState {
  text: string
  unknown: boolean
}

export const ClarificationStep = ({
  questions,
  prefill = [],
  carriedCount = 0,
  onSubmit,
  onReset,
  isBusy,
}: ClarificationStepProps) => {
  const [answers, setAnswers] = useState<AnswerState[]>(() =>
    questions.map((_, index) => ({ text: prefill[index] ?? '', unknown: false })),
  )
  const retainedCount = questions.filter((_, index) => (prefill[index] ?? '') !== '').length

  const update = (index: number, patch: Partial<AnswerState>): void => {
    setAnswers((current) =>
      current.map((item, position) => (position === index ? { ...item, ...patch } : item)),
    )
  }

  return (
    <Card>
      <SectionHeader
        title="شفاف‌سازی"
        caption="پاسخ این پرسش‌ها می‌تواند جهت تصمیم را عوض کند. اگر پاسخی ندارید، همان گزینه را انتخاب کنید؛ فرایند متوقف نمی‌شود."
      />
      <div className="space-y-5 p-5">
        {(retainedCount > 0 || carriedCount > 0) && (
          <p className="rounded-md border border-line bg-surface-sunken px-3 py-2 text-xs leading-relaxed text-ink-muted">
            {retainedCount > 0 && (
              <>
                {fa(retainedCount)} پاسخ از بررسی قبلی پیش‌پر شده است.{' '}
              </>
            )}
            {carriedCount > 0 && (
              <>
                {fa(carriedCount)} پاسخ قبلی شما حفظ شد و در همین بررسی به کار می‌رود،
                هرچند این‌بار پرسیده نشد.{' '}
              </>
            )}
            پرسش‌های خالی به‌دلیل تغییر ابعاد تصمیم تازه‌اند.
          </p>
        )}
        {questions.map((question, index) => {
          const answer = answers[index] ?? { text: '', unknown: false }
          const inputId = `clarify-${String(index)}`
          return (
            <div key={question.question} className="space-y-2">
              <div className="flex gap-2">
                <span
                  className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-sm bg-surface-sunken text-xs tabular-nums text-ink-muted"
                  aria-hidden
                >
                  {fa(index + 1)}
                </span>
                <div className="min-w-0 flex-1 space-y-2">
                  <label htmlFor={inputId} className="block text-sm font-medium text-ink">
                    {question.question}
                  </label>
                  {question.reason_it_matters !== '' && (
                    <p className="text-xs leading-relaxed text-ink-muted">
                      چرا مهم است: {question.reason_it_matters}
                    </p>
                  )}
                  <input
                    id={inputId}
                    value={answer.text}
                    disabled={answer.unknown}
                    onChange={(event) => {
                      update(index, { text: event.target.value })
                    }}
                    className="h-11 w-full rounded-md border border-line-strong bg-surface-raised px-3.5 text-sm text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none disabled:bg-surface-sunken disabled:text-ink-faint disabled:shadow-none"
                    placeholder="پاسخ شما"
                  />
                  <label className="flex w-fit items-center gap-2 text-xs text-ink-muted">
                    <input
                      type="checkbox"
                      checked={answer.unknown}
                      onChange={(event) => {
                        // انتخاب «نامعلوم» متن را پاک می‌کند تا پاسخ نیمه‌نوشته
                        // به‌عنوان پاسخ واقعی فرستاده نشود.
                        update(index, {
                          unknown: event.target.checked,
                          ...(event.target.checked ? { text: '' } : {}),
                        })
                      }}
                      className="size-4 rounded-sm border-line-strong accent-[var(--color-accent)]"
                    />
                    اطلاعاتش را ندارم
                  </label>
                </div>
              </div>
            </div>
          )
        })}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">
          <Button
            variant="ghost"
            onClick={onReset}
            icon={<RotateCcw className="size-4" aria-hidden />}
          >
            بازنشانی
          </Button>
          <Button
            variant="primary"
            loading={isBusy}
            icon={<ArrowLeft className="size-4" aria-hidden />}
            onClick={() => {
              onSubmit(
                answers.map((item) =>
                  item.unknown || item.text.trim() === '' ? null : item.text,
                ),
              )
            }}
          >
            ادامه و دریافت برآورد
          </Button>
        </div>
      </div>
    </Card>
  )
}
